
/**
 * Core: Mint Directives
 * -----------------------------------------------------------------------------
 * Vue directive that detects clicks outside of an given element.
 *
 * - No need to declare it directly in the component: `directives: { 'click-outside' }`,
 * as it will be declared automatically with `setDirectives` method
 *
 * @module click-outside
 * @example
 *
  <template>
    <div v-click-outside="closeDropdown">
      <button @click="toggleDropdown">Toggle dropdown</button>
      <div v-show="showDropdown">
        Dropdown content
      </div>
    </div>
  </template>

  <script>
    export default {
      data() {
        return {
          showDropdown: false,
        };
      },
      methods: {
        toggleDropdown() {
          this.showDropdown = !this.showDropdown;
        },
        closeDropdown() {
          this.showDropdown = false;
        },
      },
    }
  </script>
 */
export default {
  name: 'click-outside',
  directive: {

    /**
     * Called once when the directive is bound to the element but before it is
     * inserted into the DOM.
     * @param {HTMLElement} element - The element to be watched for outside clicks.
     * @param {object} binding - The object containing the callback function
     * to be executed when a click occurs outside of the element.
     */
    beforeMount: function (element, binding) {

      /**
       * This function is called when a click event occurs anywhere on the document.
       * It checks if the click occurred outside the given element and its children,
       * and if so, calls the provided method.
       * @param {Event} event - The click event object.
       */
      element.clickOutsideEvent = function (event) {
        if (!(element === event.target || element.contains(event.target))) {
          binding.value(event)
        }
      }
      document.body.addEventListener('click', element.clickOutsideEvent)
    },

    /**
     * Called once when the directive is unbound rom the element and the element
     * is removed from the DOM.
     *
     * @param {HTMLElement} element - The element that was being watched for outside clicks.
     */
    unmounted: function (element) {
      document.body.removeEventListener('click', element.clickOutsideEvent)
    },
  },
}
